import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { ChevronRight as ChevronRightIcon, HelpOutline as HelpOutlineIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getDifusionesHistorial, getEmpresas } from "../../services/api";
import showToast from "../../services/toast";

const ITEMS_PER_PAGE = 10;

const STATUS_CONFIG = {
  en_proceso: { label: "En proceso", color: "#2196f3", bg: "#e3f2fd" },
  completado: { label: "Completado", color: "#4caf50", bg: "#e8f5e9" },
  con_errores: { label: "Con errores", color: "#ff9800", bg: "#fff3e0" },
};

const DifusionesHistorial = () => {
  const navigate = useNavigate();
  const [difusiones, setDifusiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [empresas, setEmpresas] = useState([]);
  const [filtroEmpresa, setFiltroEmpresa] = useState('');

  const fetchDifusiones = useCallback(async () => {
    try {
      const params = {};
      if (filtroEmpresa) params.empresa = filtroEmpresa;
      const response = await getDifusionesHistorial(params);
      const data = response.data;
      setDifusiones(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      console.error('Error fetching difusiones:', error);
      showToast.error('Error al cargar el historial de difusiones');
    } finally {
      setLoading(false);
    }
  }, [filtroEmpresa]);

  useEffect(() => {
    getEmpresas().then(r => {
      setEmpresas(r.data.titulares || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchDifusiones();
  }, [fetchDifusiones]);

  // Auto-refresh si hay alguna difusión en proceso
  useEffect(() => {
    const hayEnProceso = difusiones.some(d => d.status === 'en_proceso');
    if (hayEnProceso) {
      const interval = setInterval(fetchDifusiones, 5000);
      return () => clearInterval(interval);
    }
  }, [difusiones, fetchDifusiones]);

  const paginated = difusiones.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(difusiones.length / ITEMS_PER_PAGE);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-PY', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Container style={{ marginLeft: "0", maxWidth: "100%" }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" style={{ fontWeight: 'bold' }}>
          Difusiones
        </Typography>
        <Button variant="contained" color="primary" onClick={() => navigate('/app/difusiones/enviar')}>
          Nueva Difusión
        </Button>
      </Box>

      {/* Filtro por empresa */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Filtrar por empresa</InputLabel>
          <Select
            value={filtroEmpresa}
            onChange={(e) => { setFiltroEmpresa(e.target.value); setCurrentPage(1); }}
            label="Filtrar por empresa"
          >
            <MenuItem value="">Todas las empresas</MenuItem>
            {empresas.map(e => (
              <MenuItem key={e.ruc_titular} value={e.ruc_titular}>
                {e.nombre_comercial}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : difusiones.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <HelpOutlineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            No hay difusiones registradas
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/app/difusiones/enviar')}>
            Crear primera difusión
          </Button>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Empresa</strong></TableCell>
                  <TableCell><strong>Plantilla</strong></TableCell>
                  <TableCell><strong>Dest.</strong></TableCell>
                  <TableCell><strong>Acept.</strong></TableCell>
                  <TableCell><strong>Entreg.</strong></TableCell>
                  <TableCell><strong>Leídos</strong></TableCell>
                  <TableCell><strong>Fallidos</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((d) => {
                  const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.completado;
                  return (
                    <TableRow
                      key={d.id}
                      hover
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/app/difusiones/${d.id}`)}
                    >
                      <TableCell>{formatDate(d.fecha_envio)}</TableCell>
                      <TableCell>{d.empresa_nombre || d.empresa_ruc}</TableCell>
                      <TableCell>{d.template_nombre}</TableCell>
                      <TableCell>{d.total_destinatarios}</TableCell>
                      <TableCell>{d.total_aceptados}</TableCell>
                      <TableCell>{d.total_entregados}</TableCell>
                      <TableCell>{d.total_leidos}</TableCell>
                      <TableCell style={{ color: d.total_fallidos > 0 ? '#f44336' : 'inherit' }}>
                        {d.total_fallidos}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            d.status === 'en_proceso'
                              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CircularProgress size={12} sx={{ color: cfg.color }} />
                                  {cfg.label}
                                </Box>
                              : cfg.label
                          }
                          size="small"
                          sx={{ color: cfg.color, bgcolor: cfg.bg, fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <ChevronRightIcon />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
              <Button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
              <Typography sx={{ alignSelf: 'center' }}>{currentPage} / {totalPages}</Typography>
              <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Siguiente</Button>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default DifusionesHistorial;
